<script setup>
/**
 * The two ways to stop using an inbox, kept deliberately apart.
 *
 * Disconnect unlinks the external account - the Facebook page, WhatsApp number,
 * Telegram bot, Twilio number - and leaves the inbox behind: its conversations stay
 * readable and searchable, it simply cannot send or receive any more. Delete removes
 * the inbox and every conversation in it.
 *
 * Only Delete asks for the inbox name to be typed. Disconnect keeps the history that
 * makes a mistake recoverable, so a typing drill there would be ceremony rather than a
 * safeguard; Delete is the one with nothing to undo.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const isDisconnected = computed(() => Boolean(props.inbox.disconnected_at));
// Web widget and API inboxes have no external account to unlink.
const canDisconnect = computed(
  () => props.inbox.disconnectable && !isDisconnected.value
);

const disconnectDialogRef = ref(null);
const deleteDialogRef = ref(null);
const deleteConfirmation = ref('');
const isDisconnecting = ref(false);
const isDeleting = ref(false);

const canConfirmDelete = computed(
  () => deleteConfirmation.value === props.inbox.name
);

const askToDisconnect = () => disconnectDialogRef.value?.open();

const askToDelete = () => {
  deleteConfirmation.value = '';
  deleteDialogRef.value?.open();
};

const confirmDisconnect = async () => {
  isDisconnecting.value = true;
  try {
    await store.dispatch('inboxes/disconnect', props.inbox.id);
    useAlert(t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.API.SUCCESS_MESSAGE'));
    disconnectDialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.API.ERROR_MESSAGE')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

const confirmDelete = async () => {
  if (!canConfirmDelete.value) return;
  isDeleting.value = true;
  try {
    await store.dispatch('inboxes/delete', props.inbox.id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    deleteDialogRef.value?.close();
    router.push(accountScopedRoute('settings_inbox_list'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <div class="w-full pt-6 mt-6 border-t border-n-weak">
    <h3 class="pb-1 text-heading-2 text-n-slate-12">
      {{ $t('INBOX_MGMT.DANGER_ZONE.TITLE') }}
    </h3>
    <p class="mb-4 text-body-main text-n-slate-11">
      {{ $t('INBOX_MGMT.DANGER_ZONE.DESCRIPTION') }}
    </p>

    <div class="flex flex-col gap-3">
      <div
        v-if="props.inbox.disconnectable"
        class="flex flex-col gap-3 p-4 border sm:flex-row sm:items-center sm:justify-between rounded-xl border-n-weak"
      >
        <div class="min-w-0">
          <p class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.TITLE') }}
          </p>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{
              isDisconnected
                ? $t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.ALREADY_DISCONNECTED')
                : $t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.DESCRIPTION')
            }}
          </p>
        </div>
        <Button
          v-if="canDisconnect"
          faded
          ruby
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.BUTTON')"
          @click="askToDisconnect"
        />
      </div>

      <div
        class="flex flex-col gap-3 p-4 border sm:flex-row sm:items-center sm:justify-between rounded-xl border-n-weak"
      >
        <div class="min-w-0">
          <p class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.DANGER_ZONE.DELETE.TITLE') }}
          </p>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.DANGER_ZONE.DELETE.DESCRIPTION') }}
          </p>
        </div>
        <Button
          ruby
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.DANGER_ZONE.DELETE.BUTTON')"
          @click="askToDelete"
        />
      </div>
    </div>

    <Dialog
      ref="disconnectDialogRef"
      type="alert"
      :title="$t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.CONFIRM.TITLE')"
      :description="
        $t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.CONFIRM.MESSAGE', {
          inboxName: props.inbox.name,
        })
      "
      :confirm-button-label="
        $t('INBOX_MGMT.DANGER_ZONE.DISCONNECT.CONFIRM.CONFIRM')
      "
      :cancel-button-label="$t('INBOX_MGMT.DANGER_ZONE.CANCEL')"
      :is-loading="isDisconnecting"
      @confirm="confirmDisconnect"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('INBOX_MGMT.DANGER_ZONE.DELETE.CONFIRM.TITLE')"
      :description="
        $t('INBOX_MGMT.DANGER_ZONE.DELETE.CONFIRM.MESSAGE', {
          inboxName: props.inbox.name,
        })
      "
      :confirm-button-label="
        $t('INBOX_MGMT.DANGER_ZONE.DELETE.CONFIRM.CONFIRM')
      "
      :cancel-button-label="$t('INBOX_MGMT.DANGER_ZONE.CANCEL')"
      :disable-confirm-button="!canConfirmDelete"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    >
      <Input
        v-model="deleteConfirmation"
        :placeholder="
          $t('INBOX_MGMT.DANGER_ZONE.DELETE.CONFIRM.PLACE_HOLDER', {
            inboxName: props.inbox.name,
          })
        "
      />
    </Dialog>
  </div>
</template>
